import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function ResendVerification() {
  const { user, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await api.post('/auth/resend-verification', { email });
      setStatus('success');
      setMessage(response.data.message);
    } catch (err: unknown) {
      setStatus('error');
      const error = err as { response?: { data?: { error?: string } } };
      setMessage(error.response?.data?.error || 'Failed to resend verification email');
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const isLoggedInUnverified = user && !user.emailVerified;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0a0a18] via-[#0d0d24] to-[#0a0a18] px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/images/dartsScheduler-trn.png"
            alt="Darts Scheduler"
            className="h-20 mx-auto mb-4"
          />
        </div>

        <div className="bg-[#1a1a3a] border border-[#2a2a4a] rounded-xl p-8">
          <h1 className="text-2xl font-bold text-white text-center mb-2">
            {isLoggedInUnverified ? 'Verify Your Email' : 'Resend Verification'}
          </h1>
          <p className="text-gray-400 text-center mb-6">
            {isLoggedInUnverified
              ? 'Please verify your email address to access the app. Check your inbox or request a new verification link below.'
              : 'Enter your email address to receive a new verification link.'}
          </p>

          {status === 'success' ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-900/30 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-gray-400 mb-6">{message}</p>
              <Link to="/login" className="text-[#1111d4] hover:underline">
                Back to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {status === 'error' && (
                <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg text-sm border border-red-800">
                  {message}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input w-full"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="btn-primary w-full"
              >
                {status === 'loading' ? 'Sending...' : 'Send Verification Email'}
              </button>

              {isLoggedInUnverified ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full text-center text-gray-500 text-sm hover:text-gray-300 transition-colors"
                >
                  Sign out and use a different account
                </button>
              ) : (
                <p className="text-center text-gray-500 text-sm">
                  Remember your password?{' '}
                  <Link to="/login" className="text-[#1111d4] hover:underline">
                    Back to Login
                  </Link>
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
